import { DeskRadioProps, DeskRadioComponents, CodeItem, CodesFilter, StyledPropsTheme } from '@my-types';
import { React, Styled } from '@common/3rd';
import { StyledComponents } from '@styles';
import { CheckableWidget, DeskComponentRegistry } from '@component';
import { Utils } from '@common';
import { ErrorReminder, PassReminder } from './input-reminder';

/**
 * 由于组件第一次构建的时候, model可能还没有值, 导致使用defaultChecked属性是false或者undefined.
 * 之后model值构建完成之后, 更新defaultChecked不能产生效果, 导致第一次onChange不能够正确响应.
 * 因此, 改用didMount和didUpdate之后直接dom设置checked值来替代defaultChecked属性.
 */
class DeskRadio<
	P extends DeskRadioProps & { codesFilter?: CodesFilter; highlight?: string[], desc?: string },
	S,
	C extends DeskRadioComponents
	> extends CheckableWidget<P, S, C> {
	radioRef: React.RefObject<any>;
	radioRefs: HTMLInputElement[];

	constructor(props: any, context?: any) {
		super(props, context);
		this.radioRef = React.createRef();
		this.radioRefs = [];
	}
	componentDidMount() {
		super.componentDidMount();
		(this.radioRefs as HTMLInputElement[]).forEach(ref => {
			ref.checked = this.getValueFromModel() === ref.value;
		})
		// this.radioRef.current.checked = this.getValueFromModel() === this.radioRef.current.value;
	}
	componentDidUpdate(prevProps: any, prevState?: any, prevContext?: any) {
		super.componentDidUpdate(prevProps, prevState, prevContext);
		// this.radioRef.current.checked = this.getValueFromModel() === this.radioRef.current.value;
	}
	protected initComponents(): C {
		return {
			RadioContainer: Styled.div.attrs({
				'data-widget': 'desk-radio-box-container'
			})`
                display: flex;
				flex-wrap: wrap;
                ${props => this.paintColor(props.theme)}
				${props => this.renderCSS(props.theme)}
			`,
			RadioVerticalContainer: Styled.div.attrs({
				'data-widget': 'desk-radio-box'
			})`
				display: flex;
				flex-direction: column;
				${props => this.renderCSS(props.theme)}
			`,
			Box: Styled.div.attrs({
				'data-widget': 'desk-radio-box'
			})`
				display: flex;
				margin-left: 16px;
				margin-right: 16px;
				${props => this.renderCSS(props.theme)}
			`,
			Radio: Styled(StyledComponents.RADIO).attrs({
				'data-widget': 'desk-radio'
			})`
				${props => this.renderCSS(props.theme)}
			`,
			RadioLabel: Styled.div.attrs({
				'data-widget': 'desk-radio-label'
			})`
				i {
					font-style: normal;
					color: ${props => props.theme.COLOR_ERROR};
				}
				${props => this.renderCSS(props.theme)}
			`,
			Desc: Styled.div.attrs({
				'data-widget': 'desk-radio-desc'
			})`
				color: ${props => props.theme.BORDER_COLOR};
				width: 100%;
				${props => this.setMargin()}
			`
		} as C;
    }
    protected doAfterCheckCompleted(): this {
		if (this.props.labellingParent) {
			this.props.labellingParent.onCheckCompleted(!this.isCheckFailOnWhatever());
		}
		return this;
	}
    renderReminder(): JSX.Element | void {
		if (this.isCheckFailOnWhatever()) {
			// 有校验失败信息
			return <ErrorReminder msg={this.getCheckFailMessage()!} />;
		} else if (this.isCheckedAnyway()) {
			// 没有校验信息, 但是曾经校验过
			return <PassReminder />;
		}
	}
	render() {
		const C = this.getComponents();
		if (this.isVerticalOrientation()) {
			return <C.RadioVerticalContainer>
				{this.renderOptions()}
			</C.RadioVerticalContainer >
		} else {
			return <C.RadioContainer>
				{this.renderOptions()}
                {this.renderReminder()}
				{this.props.desc ? <C.Desc>{this.props.desc}</C.Desc> : null}
			</C.RadioContainer >
		}
	}
	protected setMargin() {
		return this.isCheckFailOnWhatever() ? `margin-top: 36px;` : '';
	}
    protected paintColor(theme: StyledPropsTheme): string {
		return this.isCheckFailOnWhatever() ? `color: ${theme.COLOR_ERROR};margin-bottom:30px;` : '';
	}
	handleHighlight(label: string) {
		if (this.props.highlight && this.props.highlight.some(str => label.includes(str))) {
			this.props.highlight.forEach(str => {
				label = label.replace(str, `<i>${str}</i>`);
			});
		}
		return {__html: label};
	}
	renderOptions(): JSX.Element[] {
		const C = this.getComponents();
		return Utils.toArray(this.getCodes()).map((code, index) => {
			return (
				<C.Box key={index}>
					<C.Radio
						onChange={this.onValueChanged}
						onFocus={this.onInputFocus}
						onBlur={this.onInputBlur}
						disabled={!this.isEditable()}
						value={`${code.v}`}
						// ref={this.radioRef}
						ref={this.setRef}
						name={this.getPropName()}
					>
					</C.Radio>
					<C.RadioLabel><span dangerouslySetInnerHTML={this.handleHighlight(code.l)} /></C.RadioLabel>
				</C.Box>
			);
		});
	}
	private setRef = (ref: any) => {
		this.radioRefs.push(ref);
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			// console.log('setValueToDOM value', value)
			this.radioRefs.forEach(ref => {
				ref.checked = value === ref.value;
			})
			// this.radioRef.current.checked = value === this.radioRef.current.value;// ? true : false;
		}
		return this;
	}
	protected isEditable(): boolean {
		const props = this.props as any;
		return !(props.readonly || props.disabled);
	}
	private onValueChanged = (): void => {
		// const value = this.radioRef.current.checked;
		const checked = this.radioRefs.find(ref => ref.checked);
		const value = checked?.value; // this.radioRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};
	private onInputFocus = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	private onInputBlur = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};
	focus(): this {
		// this.radioRef.current.focus();
		this.radioRefs[0].focus()
		return this;
	}

	getCodes(): CodeItem[] {
		const codesFilter = this.getCodesFilter();
		if (codesFilter) {
			if (Utils.isFunction(codesFilter)) {
				return codesFilter.call(this, this.props.codes, this);
			} else {
				return codesFilter.filter.call(this, this.props.codes, this);
			}
		} else {
			return Utils.toArray(this.props.codes);
		}
	}

	protected getCodesFilter(): CodesFilter | undefined {
		return this.props.codesFilter;
	}

	protected isVerticalOrientation(): boolean {
		return this.props.verticalOrientation === true ? true : false;
	}
}

DeskComponentRegistry.register({
	radio: DeskRadio
});

export { DeskRadio };
